import React from 'react';
import { useTheme } from '../../contexts/ThemeContext';
import { cn } from '../../lib/utils';
import { ChevronLeftIcon, ChevronRightIcon } from './Icons';

export type ListPaginationProps = {
  page: number;
  totalPages: number;
  total: number;
  itemsPerPage: number;
  pageSizeOptions: number[];
  onPageChange: (page: number) => void;
  onItemsPerPageChange: (size: number) => void;
  className?: string;
  previousLabel?: string;
  nextLabel?: string;
  itemsPerPageLabel?: string;
  /** Receives rangeStart, rangeEnd, total */
  rangeLabel?: (rangeStart: number, rangeEnd: number, total: number) => React.ReactNode;
};

export const ListPagination: React.FC<ListPaginationProps> = ({
  page,
  totalPages,
  total,
  itemsPerPage,
  pageSizeOptions,
  onPageChange,
  onItemsPerPageChange,
  className,
  previousLabel = 'Προηγούμενο',
  nextLabel = 'Επόμενο',
  itemsPerPageLabel = 'Ανά σελίδα:',
  rangeLabel,
}) => {
  const { colors } = useTheme();
  const rangeStart = total === 0 ? 0 : (page - 1) * itemsPerPage + 1;
  const rangeEnd = Math.min(page * itemsPerPage, total);
  const rangeText =
    rangeLabel?.(rangeStart, rangeEnd, total) ??
    (
      <>
        Εμφάνιση <span className="font-medium">{rangeStart}</span> έως{' '}
        <span className="font-medium">{rangeEnd}</span> από{' '}
        <span className="font-medium">{total}</span>
      </>
    );

  return (
    <div className={cn('relative flex-shrink-0 border-t px-4 py-3 sm:px-6', colors.border, className)}>
      <div className="flex flex-1 justify-between sm:hidden">
        <button
          type="button"
          onClick={() => onPageChange(Math.max(1, page - 1))}
          disabled={page <= 1}
          className={cn(
            'relative inline-flex items-center gap-1.5 rounded-md border px-4 py-2 text-sm font-medium disabled:cursor-not-allowed disabled:opacity-50',
            colors.border,
            colors.surface,
            colors.textSecondary,
          )}
        >
          <ChevronLeftIcon className="h-4 w-4" />
          {previousLabel}
        </button>
        <button
          type="button"
          onClick={() => onPageChange(Math.min(totalPages, page + 1))}
          disabled={page >= totalPages}
          className={cn(
            'relative ml-3 inline-flex items-center gap-1.5 rounded-md border px-4 py-2 text-sm font-medium disabled:cursor-not-allowed disabled:opacity-50',
            colors.border,
            colors.surface,
            colors.textSecondary,
          )}
        >
          {nextLabel}
          <ChevronRightIcon className="h-4 w-4" />
        </button>
      </div>

      <div className="hidden sm:flex sm:items-center sm:justify-between">
        <p className={cn('text-sm', colors.textSecondary)}>{rangeText}</p>
        <div className="flex items-center space-x-4">
          <div className="flex items-center space-x-2">
            <label className={cn('text-sm', colors.textSecondary)}>{itemsPerPageLabel}</label>
            <select
              value={itemsPerPage}
              onChange={(e) => {
                onItemsPerPageChange(parseInt(e.target.value, 10));
              }}
              className={cn(
                'rounded-md border px-3 py-1 text-sm focus:outline-none focus:ring-2 focus:ring-offset-0',
                colors.border,
                colors.surface,
                colors.text,
                colors.primaryBorderAll,
              )}
            >
              {pageSizeOptions.map((size) => (
                <option key={size} value={size}>
                  {size}
                </option>
              ))}
            </select>
          </div>
          <nav className="relative z-0 inline-flex -space-x-px rounded-md shadow-sm" aria-label="Pagination">
            <button
              type="button"
              onClick={() => onPageChange(Math.max(1, page - 1))}
              disabled={page <= 1}
              className={cn(
                'relative inline-flex items-center rounded-l-md border px-2 py-2 text-sm font-medium disabled:cursor-not-allowed disabled:opacity-50',
                colors.border,
                colors.surface,
                colors.textMuted,
              )}
            >
              <span className="sr-only">Previous</span>
              <svg className="h-5 w-5" fill="currentColor" viewBox="0 0 20 20">
                <path
                  fillRule="evenodd"
                  d="M12.707 5.293a1 1 0 010 1.414L9.414 10l3.293 3.293a1 1 0 01-1.414 1.414l-4-4a1 1 0 010-1.414l4-4a1 1 0 011.414 0z"
                  clipRule="evenodd"
                />
              </svg>
            </button>
            {Array.from({ length: totalPages }, (_, i) => i + 1)
              .filter(
                (p) =>
                  p === 1 ||
                  p === totalPages ||
                  (p >= page - 1 && p <= page + 1),
              )
              .map((p, index, array) => {
                const showEllipsisBefore = index > 0 && array[index - 1] !== p - 1;
                return (
                  <React.Fragment key={p}>
                    {showEllipsisBefore && (
                      <span
                        className={cn(
                          'relative inline-flex items-center border px-4 py-2 text-sm font-medium',
                          colors.border,
                          colors.surface,
                          colors.textSecondary,
                        )}
                      >
                        ...
                      </span>
                    )}
                    <button
                      type="button"
                      onClick={() => onPageChange(p)}
                      className={cn(
                        'relative inline-flex items-center border px-4 py-2 text-sm font-medium',
                        p === page
                          ? cn('z-10', colors.primaryBorderAll, colors.primary, colors.primaryText)
                          : cn(colors.surface, colors.border, colors.textMuted, colors.interactiveHover),
                      )}
                    >
                      {p}
                    </button>
                  </React.Fragment>
                );
              })}
            <button
              type="button"
              onClick={() => onPageChange(Math.min(totalPages, page + 1))}
              disabled={page >= totalPages}
              className={cn(
                'relative inline-flex items-center rounded-r-md border px-2 py-2 text-sm font-medium disabled:cursor-not-allowed disabled:opacity-50',
                colors.border,
                colors.surface,
                colors.textMuted,
              )}
            >
              <span className="sr-only">Next</span>
              <svg className="h-5 w-5" fill="currentColor" viewBox="0 0 20 20">
                <path
                  fillRule="evenodd"
                  d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z"
                  clipRule="evenodd"
                />
              </svg>
            </button>
          </nav>
        </div>
      </div>
    </div>
  );
};
